import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Physics, RigidBody, CuboidCollider } from "@react-three/rapier";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { Sky, useGLTF, Environment } from "@react-three/drei";
import { DinoModel } from "./DinoGame/DinoModel";

// Preload obstacle models
useGLTF.preload("/dino/voxel_cactus_1x2.glb");
useGLTF.preload("/dino/voxel_cactus_2x1.glb");
useGLTF.preload("/dino/voxel_cactus_2x2.glb");
useGLTF.preload("/dino/3d_chrome_bird_flying.glb");

const STAND_COLLIDER = { args: [0.36, 0.65, 0.28], pos: [0,  0.65, 0] };
const DUCK_COLLIDER  = { args: [0.45, 0.35, 0.28], pos: [0,  0.35, 0] };

// ---------------------------------------------------------------------------
// Dust puff – expanding spheres that appear on dino landing
// ---------------------------------------------------------------------------
function DustPuff({ trigger }) {
  const COUNT = 5;
  const meshRefs = useRef([]);
  const ageRef   = useRef(-1);

  useEffect(() => {
    if (trigger) ageRef.current = 0;
  }, [trigger]);

  useFrame((_, dt) => {
    if (ageRef.current < 0) return;
    ageRef.current += dt;
    const t   = ageRef.current;
    const max = 0.38;

    if (t > max) {
      ageRef.current = -1;
      meshRefs.current.forEach((m) => { if (m) m.visible = false; });
      return;
    }

    const progress = t / max;
    meshRefs.current.forEach((m, i) => {
      if (!m) return;
      m.visible = true;
      const angle = (i / COUNT) * Math.PI * 2;
      const r     = 0.15 + progress * 0.55;
      m.position.set(Math.cos(angle) * r, 0.05, Math.sin(angle) * r);
      m.scale.setScalar(0.08 + progress * 0.12);
      m.material.opacity = (1 - progress) * 0.65;
    });
  });

  return (
    <group>
      {Array.from({ length: COUNT }).map((_, i) => (
        <mesh key={i} ref={(el) => (meshRefs.current[i] = el)} visible={false}>
          <sphereGeometry args={[1, 5, 5]} />
          <meshStandardMaterial color="#c8a97a" transparent opacity={0} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

// ---------------------------------------------------------------------------
// Dino
// ---------------------------------------------------------------------------
const TRAIL_COUNT = 10;

const Dino = ({ onRegisterControls, isGameOver }) => {
  const bodyRef      = useRef();
  const deathRef     = useRef();
  const deathProg    = useRef(0);
  const isJumpingRef    = useRef(false);
  const jumpProgressRef = useRef(0);
  const trailRefs    = useRef([]);
  const trailHistory = useRef([]);

  const [isJumping, setIsJumping] = useState(false);
  const [isDucking, setIsDucking] = useState(false);
  const [landed,    setLanded]    = useState(false);

  const JUMP_DURATION = 0.52;

  const doJump = useCallback(() => {
    if (isJumpingRef.current) return;
    isJumpingRef.current = true;
    jumpProgressRef.current = 0;
    setIsJumping(true);
    setLanded(false);
  }, []);

  const startDuck = useCallback(() => setIsDucking(true),  []);
  const stopDuck  = useCallback(() => setIsDucking(false), []);

  useEffect(() => {
    onRegisterControls?.({ doJump, startDuck, stopDuck });
  }, [onRegisterControls, doJump, startDuck, stopDuck]);

  useEffect(() => {
    const down = (e) => {
      if ((e.code === "Space" || e.code === "ArrowUp") && !isJumpingRef.current) {
        isJumpingRef.current = true;
        jumpProgressRef.current = 0;
        setIsJumping(true);
        setLanded(false);
      }
      if (e.code === "ArrowDown" || e.code === "KeyS") setIsDucking(true);
    };
    const up = (e) => {
      if (e.code === "ArrowDown" || e.code === "KeyS") setIsDucking(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup",   up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  useFrame((_, delta) => {
    if (!bodyRef.current) return;

    // Death fall animation
    if (isGameOver) {
      deathProg.current = Math.min(deathProg.current + delta / 0.55, 1);
      const t    = deathProg.current;
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      if (deathRef.current) {
        deathRef.current.rotation.z = -ease * Math.PI / 2;
      }
      return;
    }

    if (isJumpingRef.current) {
      jumpProgressRef.current = Math.min(jumpProgressRef.current + delta / JUMP_DURATION, 1);
      const newY = 3.2 * Math.sin(jumpProgressRef.current * Math.PI);
      bodyRef.current.setTranslation({ x: 0, y: newY, z: 0 }, true);

      // Sample trail position
      trailHistory.current.push({ y: newY * 0.72, age: 0 });
      if (trailHistory.current.length > TRAIL_COUNT) trailHistory.current.shift();

      if (jumpProgressRef.current >= 1) {
        isJumpingRef.current = false;
        setIsJumping(false);
        setLanded(true);
        setTimeout(() => setLanded(false), 280);
      }
    } else {
      bodyRef.current.setTranslation({ x: 0, y: 0, z: 0 }, true);
    }

    // Age trail and update meshes
    trailHistory.current.forEach(p => { p.age += delta; });
    trailHistory.current = trailHistory.current.filter(p => p.age < 0.45);
    trailRefs.current.forEach((m, i) => {
      const p = trailHistory.current[trailHistory.current.length - 1 - i];
      if (!m) return;
      if (!p) { m.visible = false; return; }
      const fade = 1 - p.age / 0.45;
      m.visible = true;
      m.position.y = p.y;
      m.scale.setScalar(fade * 0.09 + 0.02);
      m.material.opacity = fade * 0.22;
    });
  });

  const col = isDucking ? DUCK_COLLIDER : STAND_COLLIDER;

  return (
    <>
      <RigidBody ref={bodyRef} position={[0, 0, 0]} lockRotations colliders={false}>
        <CuboidCollider key={isDucking ? "duck" : "stand"} args={col.args} position={col.pos} />
        <group scale={0.72} ref={deathRef}>
          <DinoModel isJumping={isJumping} isDucking={isDucking} isDead={isGameOver} landed={landed} isRunning={true} />
        </group>
      </RigidBody>
      <DustPuff trigger={landed} />
      {/* Jump trail */}
      <group>
        {Array.from({ length: TRAIL_COUNT }).map((_, i) => (
          <mesh key={i} ref={(el) => (trailRefs.current[i] = el)} visible={false}>
            <sphereGeometry args={[1, 6, 6]} />
            <meshStandardMaterial color="#ffe8cc" transparent opacity={0} depthWrite={false} />
          </mesh>
        ))}
      </group>
    </>
  );
};

// ---------------------------------------------------------------------------
// Camera
// ---------------------------------------------------------------------------
function CameraController() {
  useFrame(({ camera }) => {
    camera.position.set(10, 2, 0);
    camera.lookAt(0, 1, -4);
  });
  return null;
}

// ---------------------------------------------------------------------------
// Ground – with scrolling dash marks
// ---------------------------------------------------------------------------
const PLATFORM_W = 25;
const PLATFORM_H = 1.0;
const TILE_LEN   = 60;

// Dash Z positions relative to tile centre
const DASH_ZS = [-22, -13, -4, 5, 14, 23, 32].map((z) => z - TILE_LEN / 2 + TILE_LEN / 2);

function Ground({ worldZRef }) {
  const tileRefs = useRef([]);

  const sandTex = useMemo(() => {
    const W = 256, H = 256;
    const data = new Uint8Array(W * H * 4);
    for (let i = 0; i < W * H; i++) {
      const n = Math.random();
      data[i * 4 + 0] = Math.floor(185 + n * 35);
      data[i * 4 + 1] = Math.floor(155 + n * 28);
      data[i * 4 + 2] = Math.floor(100 + n * 20);
      data[i * 4 + 3] = 255;
    }
    const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(14, 14);
    tex.needsUpdate = true;
    return tex;
  }, []);

  useFrame(() => {
    const wz     = worldZRef.current;
    const offset = ((-wz) % TILE_LEN + TILE_LEN) % TILE_LEN;
    tileRefs.current.forEach((group, i) => {
      if (group) group.position.z = offset + (i - 2) * TILE_LEN;
    });
  });

  return (
    <>
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[PLATFORM_W, PLATFORM_H, 500]} position={[0, -PLATFORM_H, -200]} />
      </RigidBody>

      {[0, 1, 2, 3].map((i) => (
        <group key={i} ref={(el) => (tileRefs.current[i] = el)}>
          {/* Ground slab */}
          <mesh position={[0, -PLATFORM_H, 0]}>
            <boxGeometry args={[PLATFORM_W * 2, PLATFORM_H * 2, TILE_LEN]} />
            <meshStandardMaterial color="#c8a870" roughness={0.95} />
          </mesh>
          {/* Top surface – sandy noise texture */}
          <mesh position={[0, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[PLATFORM_W * 2, TILE_LEN]} />
            <meshStandardMaterial map={sandTex} roughness={1} />
          </mesh>
          {/* Dash marks */}
          {DASH_ZS.map((dz, j) => (
            <mesh key={j} position={[0, 0.008, dz]}>
              <boxGeometry args={[PLATFORM_W * 2, 0.01, 0.07]} />
              <meshStandardMaterial color="#b09060" roughness={1} />
            </mesh>
          ))}
        </group>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sand dunes – three tile patterns with varied sizes/positions/colours
// ---------------------------------------------------------------------------
// Single pattern reused across all tiles — identical layout prevents flicker on recycle
const DUNES = [
  { z: -24, zs: 4.2, sy: 0.30, r: 3.0, color: "#c89058" },
  { z:   8, zs: 2.4, sy: 1.20, r: 2.0, color: "#d4a870" },
  { z:  36, zs: 3.6, sy: 0.58, r: 2.4, color: "#bf8a52" },
];

function SandDunes({ worldZRef }) {
  const tileRefs  = useRef([]);
  const duneMats  = useMemo(() => DUNES.map(d => new THREE.MeshStandardMaterial({ color: d.color, roughness: 0.9 })), []);

  useFrame(() => {
    const wz  = worldZRef.current;
    const off = ((-wz) % TILE_LEN + TILE_LEN) % TILE_LEN;
    tileRefs.current.forEach((g, i) => { if (g) g.position.z = off + (i - 1) * TILE_LEN; });
  });

  return (
    <>
      {[0, 1, 2].map((i) => (
        <group key={i} ref={(el) => (tileRefs.current[i] = el)} position={[-16, 0, 0]}>
          {DUNES.map((d, j) => (
            <mesh key={j} position={[0, -0.4, d.z]} scale={[1.2, d.sy, d.zs]} material={duneMats[j]}>
              <sphereGeometry args={[d.r, 7, 5, 0, Math.PI * 2, 0, Math.PI / 2]} />
            </mesh>
          ))}
        </group>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Clouds
// ---------------------------------------------------------------------------
const CLOUD_DEFS = [
  { z: -24, y: 8.0, blobs: [{ dx: 0, dy: 0, r: 1.1 }, { dx: 1.1, dy: -0.2, r: 0.85 }, { dx: -1.0, dy: -0.15, r: 0.8 }] },
  { z:   5, y: 9.5, blobs: [{ dx: 0, dy: 0, r: 1.4 }, { dx: 1.3, dy: -0.1, r: 1.0  }, { dx:-1.2,  dy: -0.2, r: 0.9 }, { dx: 0.5, dy: 0.5, r: 0.7 }] },
  { z:  30, y: 7.5, blobs: [{ dx: 0, dy: 0, r: 0.8 }, { dx: 0.8, dy: -0.1, r: 0.65 }] },
  { z: -50, y: 8.8, blobs: [{ dx: 0, dy: 0, r: 1.0 }, { dx: 1.0, dy: -0.2, r: 0.75 }] },
];

function Clouds({ worldZRef }) {
  const tileRefs  = useRef([]);
  const cloudMat  = useMemo(() => new THREE.MeshStandardMaterial({ color: "#fff8f0", roughness: 1 }), []);

  useFrame(() => {
    const wz  = worldZRef.current;
    const off = ((-wz) % TILE_LEN + TILE_LEN) % TILE_LEN;
    tileRefs.current.forEach((g, i) => { if (g) g.position.z = off + (i - 1) * TILE_LEN; });
  });

  return (
    <>
      {[0, 1, 2].map((i) => (
        <group key={i} ref={(el) => (tileRefs.current[i] = el)}>
          {CLOUD_DEFS.map((cloud, j) => (
            <group key={j} position={[-4, cloud.y, cloud.z]}>
              {cloud.blobs.map((blob, k) => (
                <mesh key={k} position={[blob.dx, blob.dy, 0]} material={cloudMat}>
                  <sphereGeometry args={[blob.r, 6, 5]} />
                </mesh>
              ))}
            </group>
          ))}
        </group>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Cactus obstacle – green
// ---------------------------------------------------------------------------
const CACTUS_MODELS = [
  { path: "/dino/voxel_cactus_1x2.glb", scale: 0.55, collider: [0.25, 0.75, 0.25], cy: 0.75 },
  { path: "/dino/voxel_cactus_2x1.glb", scale: 0.7,  collider: [0.45, 0.40, 0.25], cy: 0.40 },
  { path: "/dino/voxel_cactus_2x2.glb", scale: 0.6,  collider: [0.45, 0.75, 0.25], cy: 0.75 },
];

function CactusObstacle({ obsWorldZ, worldZRef, modelIndex, onCollide, tall }) {
  const def    = CACTUS_MODELS[modelIndex % CACTUS_MODELS.length];
  const rbRef  = useRef();
  const { scene } = useGLTF(def.path);
  const tm = tall ? 2.0 : 1.0;

  const cloned = useMemo(() => {
    const c = scene.clone(true);
    c.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = true;
        o.receiveShadow = true;
        o.material = new THREE.MeshStandardMaterial({
          color: tall ? "#2e6b28" : "#4a8c40",
          roughness: 0.75,
        });
      }
    });
    return c;
  }, [scene, tall]);

  useFrame(() => {
    if (!rbRef.current) return;
    rbRef.current.setNextKinematicTranslation({ x: 0, y: 0, z: obsWorldZ - worldZRef.current });
  });

  return (
    <RigidBody ref={rbRef} type="kinematicPosition"
               position={[0, 0, obsWorldZ - worldZRef.current]}
               onCollisionEnter={onCollide} colliders={false}>
      <CuboidCollider
        args={[def.collider[0], def.collider[1] * tm, def.collider[2]]}
        position={[0, def.cy * tm, 0]}
      />
      <primitive object={cloned} scale={def.scale * tm} />
    </RigidBody>
  );
}

// ---------------------------------------------------------------------------
// Bird obstacle – slate blue
// ---------------------------------------------------------------------------
function BirdObstacle({ obsWorldZ, worldZRef, onCollide }) {
  const { scene, animations } = useGLTF("/dino/3d_chrome_bird_flying.glb");
  const rbRef    = useRef();
  const mixerRef = useRef();

  const cloned = useMemo(() => {
    const c = SkeletonUtils.clone(scene);
    c.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = true;
        o.material = new THREE.MeshStandardMaterial({ color: "#1e3a58", roughness: 0.7 });
      }
    });
    return c;
  }, [scene]);

  useEffect(() => {
    const clip = animations.find((a) => a.name === "Flying") ?? animations[0];
    if (!clip) return;
    const mixer = new THREE.AnimationMixer(cloned);
    mixer.clipAction(clip).play();
    mixerRef.current = mixer;
    return () => mixer.stopAllAction();
  }, [cloned, animations]);

  useFrame((_, dt) => {
    mixerRef.current?.update(dt);
    if (rbRef.current)
      rbRef.current.setNextKinematicTranslation({ x: 0, y: 1.0, z: obsWorldZ - worldZRef.current });
  });

  return (
    <RigidBody ref={rbRef} type="kinematicPosition"
               position={[0, 1.0, obsWorldZ - worldZRef.current]}
               onCollisionEnter={onCollide} colliders={false}>
      <CuboidCollider args={[0.45, 0.22, 0.2]} position={[0, 0, 0]} />
      <primitive object={cloned} scale={0.6} rotation={[0, Math.PI / 2, 0]} />
    </RigidBody>
  );
}

// ---------------------------------------------------------------------------
// GameScene
// ---------------------------------------------------------------------------
function GameScene({ isGameOver, setIsGameOver, setScore, speed, setSpeed, onRegisterControls, onSpeedUp }) {
  const [obstacles, setObstacles] = useState([]);
  const worldZ         = useRef(0);
  const lastObstacleAt = useRef(0);
  const lastScoreRef   = useRef(0);

  useEffect(() => {
    let id;
    let last = performance.now();

    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      if (!isGameOver) {
        worldZ.current -= speed * 60 * dt;

        const newScore = Math.floor(-worldZ.current / 10);
        if (newScore !== lastScoreRef.current) {
          lastScoreRef.current = newScore;
          setScore(newScore);
        }

        const t      = Date.now();
        const minGap = Math.max(1000, 2600 - speed * 700);
        if (t - lastObstacleAt.current > minGap) {
          const isBird     = speed > 0.45 && Math.random() < 0.28;
          const modelIndex = Math.floor(Math.random() * 3);
          const isTall     = !isBird && speed > 0.3 && Math.random() < 0.3;
          lastObstacleAt.current = t;

          setObstacles((prev) => {
            const pruned = prev.filter((o) => (o.worldZ - worldZ.current) < 10);
            return [...pruned, { id: t, worldZ: worldZ.current - 55, lane: 0, isBird, modelIndex, isTall }];
          });
        }
      }
      id = requestAnimationFrame(tick);
    };

    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [isGameOver, speed, setScore]);

  const onSpeedUpRef = useRef(onSpeedUp);
  useEffect(() => { onSpeedUpRef.current = onSpeedUp; }, [onSpeedUp]);

  useEffect(() => {
    if (isGameOver) return;
    const iv = setInterval(() => {
      setSpeed((s) => {
        const next = Math.min(s + 0.04, 2.2);
        if (next > s) onSpeedUpRef.current?.();
        return next;
      });
    }, 8000);
    return () => clearInterval(iv);
  }, [isGameOver, setSpeed]);

  const handleCollision = () => { if (!isGameOver) setIsGameOver(true); };

  return (
    <>
      <CameraController />

      <Environment preset="sunset" resolution={256} />
      <Sky sunPosition={[100, 10, 100]} turbidity={8} rayleigh={2} mieCoefficient={0.004} mieDirectionalG={0.82} />

      {/* Background scenery */}
      <SandDunes worldZRef={worldZ} />
      <Clouds worldZRef={worldZ} />

      <Physics gravity={[0, 0, 0]}>
        <Dino onRegisterControls={onRegisterControls} isGameOver={isGameOver} />
        <Ground worldZRef={worldZ} />

        {obstacles.map((obs) => obs.isBird
          ? <BirdObstacle   key={obs.id} obsWorldZ={obs.worldZ} worldZRef={worldZ} onCollide={handleCollision} />
          : <CactusObstacle key={obs.id} obsWorldZ={obs.worldZ} worldZRef={worldZ} modelIndex={obs.modelIndex} onCollide={handleCollision} tall={obs.isTall} />
        )}
      </Physics>
    </>
  );
}

// ---------------------------------------------------------------------------
// DinoGame – root component
// ---------------------------------------------------------------------------
export default function DinoGame() {
  const [started,      setStarted]      = useState(false);
  const [gameOver,     setGameOver]     = useState(false);
  const [score,        setScore]        = useState(0);
  const [highScore,    setHighScore]    = useState(0);
  const [speed,        setSpeed]        = useState(0.18);
  const [gameKey,      setGameKey]      = useState(0);
  const [speedLevel,   setSpeedLevel]   = useState(1);
  const [showSpeedUp,  setShowSpeedUp]  = useState(false);
  const speedPopupTimer = useRef(null);
  const [isPortrait, setIsPortrait] = useState(
    () => window.innerHeight > window.innerWidth
  );

  const controlsRef = useRef(null);

  useEffect(() => {
    const check = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize",           check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize",           check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  useEffect(() => {
    const block = (e) => e.preventDefault();
    document.addEventListener("contextmenu", block);
    return () => document.removeEventListener("contextmenu", block);
  }, []);

  const handleSpeedUp = useCallback(() => {
    setSpeedLevel((l) => l + 1);
    setShowSpeedUp(true);
    clearTimeout(speedPopupTimer.current);
    speedPopupTimer.current = setTimeout(() => setShowSpeedUp(false), 1800);
  }, []);

  const start = () => {
    setStarted(true); setGameOver(false); setScore(0);
    setSpeed(0.18); setSpeedLevel(1); setShowSpeedUp(false);
    setGameKey((k) => k + 1);
  };
  const restart = () => {
    setHighScore((h) => Math.max(h, score));
    setGameOver(false); setScore(0);
    setSpeed(0.18); setSpeedLevel(1); setShowSpeedUp(false);
    setGameKey((k) => k + 1);
  };

  if (isMobile && isPortrait) {
    return (
      <div style={{ width: "100vw", height: "100vh", background: "#FBDB93", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "milker, sans-serif" }}>
        <div style={{ fontSize: "64px", marginBottom: "16px" }}>📱</div>
        <p style={{ fontSize: "1.2rem", color: "#641B2E", fontWeight: "bold", textAlign: "center", padding: "0 32px" }}>
          Tilt your phone to landscape to play!
        </p>
      </div>
    );
  }

  const hint = "⬆ / Space — Jump  |  ⬇ / S — Duck";

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#e8c97a", fontFamily: "'Segoe UI', sans-serif" }}>
      <Canvas
        camera={{ fov: 60 }}
        dpr={[1, 1.5]}
        gl={{ antialias: false, powerPreference: "high-performance" }}
        performance={{ min: 0.5 }}
      >
        {started && (
          <GameScene
            key={gameKey}
            isGameOver={gameOver}
            setIsGameOver={setGameOver}
            setScore={setScore}
            speed={speed}
            setSpeed={setSpeed}
            onRegisterControls={(c) => { controlsRef.current = c; }}
            onSpeedUp={handleSpeedUp}
          />
        )}
      </Canvas>

      {/* ── Mobile jump / duck buttons ── */}
      {isMobile && started && !gameOver && (
        <div style={ui.mobileButtons}>
          <button
            style={ui.mobileBtn}
            onTouchStart={(e) => { e.preventDefault(); controlsRef.current?.doJump(); }}
          >▲</button>
          <button
            style={ui.mobileBtn}
            onTouchStart={(e) => { e.preventDefault(); controlsRef.current?.startDuck(); }}
            onTouchEnd={(e)   => { e.preventDefault(); controlsRef.current?.stopDuck();  }}
            onContextMenu={(e) => e.preventDefault()}
          >▼</button>
        </div>
      )}

      {/* ── Start screen ── */}
      {!started && (
        <div style={ui.overlay}>
          <h1 style={ui.title}>🦕 3D Dino Runner</h1>
          <div style={ui.controls}>
            {isMobile ? (
              <>
                <span>▲ — Jump</span>
                <span>▼ — Duck</span>
              </>
            ) : (
              <>
                <span>⬆ / Space — Jump</span>
                <span>⬇ / S — Duck</span>
              </>
            )}
          </div>
          <button style={ui.btn} onClick={start}>Start Game</button>
        </div>
      )}

      {/* ── HUD ── */}
      {started && (
        <>
          <div style={ui.hud}>
            <span>Score <strong>{score}</strong></span>
            <span style={{ marginLeft: 20, opacity: 0.7 }}>Best {highScore}</span>
          </div>
          <div style={ui.hint}>{hint}</div>
          {showSpeedUp && (
            <div style={ui.speedPopup}>
              ⚡ Speed Up! <span style={{ opacity: 0.75, fontSize: "0.85em" }}>Level {speedLevel}</span>
            </div>
          )}
        </>
      )}

      {/* ── Game Over ── */}
      {gameOver && (
        <div style={ui.scrim} />
      )}
      {gameOver && (
        <div style={ui.overlay}>
          <h2 style={{ ...ui.title, fontSize: "2rem" }}>Game Over</h2>
          <p style={{ color: "#fff", margin: "8px 0 4px", fontSize: "1.1rem" }}>Score: <strong>{score}</strong></p>
          <p style={{ color: "#ffd54f", margin: "0 0 24px", fontSize: "0.95rem" }}>Best: {Math.max(highScore, score)}</p>
          <button style={ui.btn} onClick={restart}>Play Again</button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const ui = {
  overlay: {
    position: "absolute", top: "50%", left: "50%",
    transform: "translate(-50%, -50%)",
    background: "rgba(10,10,30,0.82)",
    backdropFilter: "blur(6px)",
    padding: "36px 44px", borderRadius: 16,
    color: "#fff", textAlign: "center",
    boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
    zIndex: 10,
  },
  title: {
    fontSize: "2.6rem", margin: "0 0 18px",
    textShadow: "0 2px 8px rgba(0,0,0,0.6)",
    letterSpacing: 1,
  },
  controls: {
    display: "flex", flexDirection: "column", gap: 6,
    marginBottom: 24, fontSize: "0.95rem",
    color: "#b0c4de", lineHeight: 1.7,
  },
  btn: {
    background: "linear-gradient(135deg, #43a047, #2e7d32)",
    border: "none", color: "#fff",
    padding: "13px 36px", fontSize: "1rem",
    borderRadius: 8, cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
    transition: "transform 0.15s",
  },
  hud: {
    position: "absolute", top: 18, right: 22,
    background: "rgba(10,10,30,0.65)",
    backdropFilter: "blur(4px)",
    padding: "10px 18px", borderRadius: 10,
    color: "#fff", fontSize: "1.05rem",
    zIndex: 10,
  },
  hint: {
    position: "absolute", bottom: 18, left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(10,10,30,0.55)",
    backdropFilter: "blur(4px)",
    padding: "8px 20px", borderRadius: 20,
    color: "#b0c4de", fontSize: "0.85rem",
    zIndex: 10, whiteSpace: "nowrap",
  },
  mobileButtons: {
    position: "absolute", bottom: 24, left: 24,
    display: "flex", flexDirection: "column", gap: 12,
    zIndex: 10,
  },
  scrim: {
    position: "absolute", inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(4px)",
    zIndex: 9,
  },
  speedPopup: {
    position: "absolute", top: 70, right: 22,
    background: "linear-gradient(135deg, #f59e0b, #d97706)",
    padding: "10px 20px", borderRadius: 10,
    color: "#fff", fontSize: "1.1rem", fontWeight: "bold",
    boxShadow: "0 4px 18px rgba(0,0,0,0.35)",
    zIndex: 10,
    animation: "fadeInOut 1.8s ease forwards",
  },
  mobileBtn: {
    width: 64, height: 64,
    borderRadius: "50%",
    background: "rgba(10,10,30,0.65)",
    backdropFilter: "blur(6px)",
    border: "2px solid rgba(255,255,255,0.25)",
    color: "#fff", fontSize: "1.5rem",
    cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    touchAction: "none",
  },
};
